import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { motion } from 'framer-motion';
import { CheckCircle, AlertCircle, Shield, Heart, Sword, Crosshair } from 'lucide-react';
import {
  submitMidnightSurvey,
  getUserMidnightSurvey,
  ROLE_CLASSES,
  RoleType,
  GeneralThoughtsOption,
  RoleInterest,
} from '../services/midnightSurveyService';

const MidnightSurvey: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Form state
  const [generalThoughts, setGeneralThoughts] = useState<GeneralThoughtsOption | ''>('');
  
  // Role states
  const [roles, setRoles] = useState<{ [key in RoleType]: RoleInterest }>({
    'Tank': { absolutelyNot: false, interestLevel: 1, classes: [] },
    'Healer': { absolutelyNot: false, interestLevel: 1, classes: [] },
    'Melee DPS': { absolutelyNot: false, interestLevel: 1, classes: [] },
    'Ranged DPS': { absolutelyNot: false, interestLevel: 1, classes: [] },
  });

  const roleIcons = {
    'Tank': Shield,
    'Healer': Heart,
    'Melee DPS': Sword,
    'Ranged DPS': Crosshair,
  };

  // Redirect to login if not authenticated
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    
    // Load user's existing survey if it exists
    loadUserSurvey();
  }, [authLoading, user, navigate]);

  const loadUserSurvey = async () => {
    if (!user) return;
    const userSurvey = await getUserMidnightSurvey(user.uid);
    if (userSurvey) {
      setGeneralThoughts(userSurvey.generalThoughts);
      setRoles(userSurvey.roles);
    }
  };

  const handleRoleToggle = (roleType: RoleType) => {
    setRoles(prev => ({
      ...prev,
      [roleType]: {
        ...prev[roleType],
        absolutelyNot: !prev[roleType].absolutelyNot,
        // Reset interest level and classes if marking as "absolutely not"
        interestLevel: !prev[roleType].absolutelyNot ? 1 : prev[roleType].interestLevel,
        classes: !prev[roleType].absolutelyNot ? [] : prev[roleType].classes,
      }
    }));
  };

  const handleInterestChange = (roleType: RoleType, level: number) => {
    setRoles(prev => ({
      ...prev,
      [roleType]: {
        ...prev[roleType],
        interestLevel: level,
      }
    }));
  };

  const handleClassToggle = (roleType: RoleType, className: string) => {
    setRoles(prev => {
      const role = prev[roleType];
      const newClasses = role.classes.includes(className)
        ? role.classes.filter(c => c !== className)
        : [...role.classes, className];
      
      return {
        ...prev,
        [roleType]: {
          ...role,
          classes: newClasses,
        }
      };
    });
  };

  const handleSubmitSurvey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    if (!generalThoughts) {
      setError('Please answer the general thoughts question');
      return;
    }
    
    setError(null);
    setSuccess(null);
    setLoading(true);
    
    const result = await submitMidnightSurvey(
      user.uid,
      user.username || user.email || '',
      generalThoughts as GeneralThoughtsOption,
      roles
    );
    
    if (result) {
      setError(result);
    } else {
      setSuccess('Survey submitted successfully!');
    }
    
    setLoading(false);
  };

  if (authLoading) {
    return (
      <div className="page-container bg-surface flex items-center justify-center">
        <p className="text-text-secondary">Loading...</p>
      </div>
    );
  }

  return (
    <div className="page-container bg-surface">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">
              Midnight Season 2 Survey
            </span>
          </h1>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Help us prepare for Midnight Season 2 by sharing your role and class preferences
          </p>
        </motion.div>
        
        {/* Form */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="glass p-8 rounded-xl max-w-5xl mx-auto"
        >
          <form onSubmit={handleSubmitSurvey} className="space-y-8">
            {/* Username Display */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Your Username
              </label>
              <div className="w-full px-4 py-3 bg-surface-light border border-border rounded-lg text-text-primary font-semibold">
                {user?.username || user?.email}
              </div>
            </div>
            
            {/* Section 1: General Thoughts */}
            <div className="border-b border-border pb-6">
              <h2 className="text-2xl font-bold mb-4">
                <span className="gradient-text">General Thoughts on Role/Class/Spec</span>
              </h2>
              <p className="text-text-secondary mb-4">
                What are your thoughts on role/class/spec in Midnight Season 2?
              </p>
              <div className="space-y-3">
                {[
                  'I know what I want to play',
                  'I have some ideas on what I want to play',
                  'I am open to filling raid team needs',
                  "I don't know what I want to play"
                ].map((option) => (
                  <label
                    key={option}
                    className="flex items-center gap-3 p-4 bg-surface-light rounded-lg cursor-pointer hover:bg-surface transition-colors border-2 border-transparent hover:border-primary/30"
                  >
                    <input
                      type="radio"
                      name="generalThoughts"
                      value={option}
                      checked={generalThoughts === option}
                      onChange={(e) => setGeneralThoughts(e.target.value as GeneralThoughtsOption)}
                      className="w-5 h-5 text-primary focus:ring-primary focus:ring-2"
                    />
                    <span className="text-text-primary font-medium">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Section 2: Role Interest */}
            <div>
              <h2 className="text-2xl font-bold mb-2">
                <span className="gradient-text">Interest in Each Role</span>
              </h2>
              <p className="text-text-secondary mb-6">
                What is your interest in each role? (1: Very Low - 5: Very Interested)
              </p>

              
              <div className="space-y-8">
                {(Object.keys(ROLE_CLASSES) as RoleType[]).map((roleType) => {
                  const role = roles[roleType];
                  const IconComponent = roleIcons[roleType];
                  const availableClasses = ROLE_CLASSES[roleType];
                  
                  return (
                    <div
                      key={roleType}
                      className={`p-6 rounded-xl border-2 ${
                        role.absolutelyNot
                          ? 'bg-surface-light border-border opacity-60'
                          : 'bg-surface-light border-primary/30'
                      }`}
                    >
                      <div className="flex items-start gap-4 mb-4">
                        <div className={`p-3 rounded-lg ${
                          role.absolutelyNot ? 'bg-surface' : 'bg-primary/20'
                        }`}>
                          <IconComponent
                            className={role.absolutelyNot ? 'text-text-secondary' : 'text-primary'}
                            size={32}
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-xl font-semibold text-text-primary">
                              {roleType}
                            </h3>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={role.absolutelyNot}
                                onChange={() => handleRoleToggle(roleType)}
                                className="w-5 h-5 text-primary focus:ring-primary focus:ring-2 rounded"
                              />
                              <span className="text-sm text-text-secondary">Absolutely not</span>
                            </label>
                          </div>
                          
                          {!role.absolutelyNot && (
                            <>
                              {/* Interest Level Slider */}
                              <div className="mb-6">
                                <div className="flex items-center justify-between px-2 mb-2">
                                  <span className="text-sm text-text-secondary">Very Low (1)</span>
                                  <span className="text-sm text-text-secondary">Very Interested (5)</span>
                                </div>
                                <input
                                  type="range"
                                  min="1"
                                  max="5"
                                  value={role.interestLevel}
                                  onChange={(e) => handleInterestChange(roleType, parseInt(e.target.value))}
                                  className="w-full h-2 bg-surface rounded-lg appearance-none cursor-pointer slider"
                                  style={{
                                    background: `linear-gradient(to right, #ef4444 0%, #f97316 25%, #eab308 50%, #84cc16 75%, #22c55e 100%)`
                                  }}
                                />
                                <div className="flex justify-center mt-3">
                                  <div className="px-6 py-2 rounded-lg border-2 border-primary/50 bg-primary/10">
                                    <div className="text-center">
                                      <div className="text-2xl font-bold text-text-primary">{role.interestLevel}</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Class Selection */}
                              <div>
                                <p className="text-sm font-medium text-text-secondary mb-3">
                                  Select classes you would play in this role:
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                  {availableClasses.map((className) => (
                                    <label
                                      key={className}
                                      className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-all border-2 ${
                                        role.classes.includes(className)
                                          ? 'bg-primary/20 border-primary text-primary'
                                          : 'bg-surface border-border hover:border-primary/30'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={role.classes.includes(className)}
                                        onChange={() => handleClassToggle(roleType, className)}
                                        className="w-4 h-4 text-primary focus:ring-primary focus:ring-2 rounded"
                                      />
                                      <span className="text-sm font-medium text-text-primary">
                                        {className}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Error/Success Messages — placed near submit so they're visible after scrolling */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 flex items-center gap-3"
              >
                <AlertCircle className="text-red-400 shrink-0" size={20} />
                <p className="text-red-400">{error}</p>
              </motion.div>
            )}

            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 flex items-center gap-3"
              >
                <CheckCircle className="text-green-400 shrink-0" size={20} />
                <p className="text-green-400">{success}</p>
              </motion.div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !generalThoughts}
              className="w-full px-6 py-4 bg-gradient-to-r from-primary to-secondary text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {loading ? 'Submitting...' : 'Submit Survey'}
            </button>
            
            {!generalThoughts && (
              <p className="text-sm text-text-secondary text-center">
                Please answer the general thoughts question to continue
              </p>
            )}
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default MidnightSurvey;

